import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const MarkSaleModal = ({ agent, onClose, onSaved }) => {
  const [search, setSearch] = useState('');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const load = async (q = '') => {
    try {
      setLoading(true);
      const res = await chemicalService.getUnmarkedSales({ search: q, limit: 40 });
      setSales(res.data || []);
    } catch (err) {
      console.error('Error loading unmarked sales:', err);
      alert('❌ Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const handleMark = async (sale) => {
    if (markingId) return;
    if (!window.confirm(`Mark invoice ${sale.invoice_no} as ${agent.name}'s order?`)) return;
    try {
      setMarkingId(sale.id);
      await chemicalService.markSale({ agent_id: agent.id, sale_id: sale.id });
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to mark sale';
      alert('❌ ' + msg);
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>+ Mark a Sale as {agent.name}'s</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Pick an unclaimed invoice — its {agent.profit_share_pct}% net profit share will be credited to {agent.name}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={{ padding: '20px 30px' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.input}
            placeholder="🔍 Search by invoice no. or customer name..."
            autoFocus
          />

          <div style={styles.list}>
            {loading ? (
              <div style={styles.empty}>Loading...</div>
            ) : sales.length === 0 ? (
              <div style={styles.empty}>
                {search ? 'No matching unmarked sales.' : 'No unmarked sales available.'}
              </div>
            ) : (
              sales.map((s) => (
                <div key={s.id} style={styles.saleRow}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.invNo}>{s.invoice_no}</div>
                    <div style={styles.saleMeta}>
                      {s.customer_name} · {fmtDate(s.invoice_date)}
                    </div>
                  </div>
                  <div style={styles.saleAmt}>{fmtMoney(s.grand_total)}</div>
                  <button
                    style={styles.markBtn}
                    onClick={() => handleMark(s)}
                    disabled={markingId === s.id}
                  >
                    {markingId === s.id ? '⏳' : 'Mark'}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button type="button" onClick={onClose} style={styles.cancelButton}>Close</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '620px', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  input: { width: '100%', padding: '11px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '16px' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  empty: { padding: '30px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' },
  saleRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e0e0e0' },
  invNo: { fontSize: '14px', fontWeight: '700', color: '#2c3e50' },
  saleMeta: { fontSize: '12px', color: '#7f8c8d', marginTop: '2px' },
  saleAmt: { fontSize: '14px', fontWeight: '700', color: '#2c3e50' },
  markBtn: { padding: '8px 18px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default MarkSaleModal;